import React, { useEffect, useState } from "react";
import { Dropdown, IDropdownOption, ResponsiveMode } from "@fluentui/react";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import { FormatImage } from "../../api/models/format-image";

export const DEFAULT_IMAGE_EXTENSION = "png";
export const DEFAULT_IMAGE_RESOLUTION_MAP: Map<string, number> = new Map([
  ["jpg", 150],
  ["png", 150],
  ["svg", undefined],
  ["emf", undefined],
  ["tiff", 150],
]);

export const DEFAULT_FORMAT_IMAGE: FormatImage = {
  imageExtension: DEFAULT_IMAGE_EXTENSION,
  imageResolution: DEFAULT_IMAGE_RESOLUTION_MAP.get(DEFAULT_IMAGE_EXTENSION),
};

export const SUPPORTED_ONLY_ON_WORD_DESKTOP = ["svg", "emf", "tiff"];

const imageExtensionOptions: IDropdownOption[] = [
  { key: "jpg", text: "cof.base.jpeg" },
  { key: "png", text: "cof.base.png" },
  { key: "svg", text: "cof.base.svg" },
  { key: "emf", text: "cof.base.emf" },
  { key: "tiff", text: "cof.base.tiff" },
];

const imageResolutionOptionsMap: Map<string, IDropdownOption[]> = new Map([
  [
    "jpg",
    [
      { key: 150, text: "cof.base.resolutionlow" },
      { key: 300, text: "cof.base.resolutionstandard" },
      { key: 600, text: "cof.base.resolutionhigh" },
    ],
  ],
  [
    "png",
    [
      { key: 150, text: "cof.base.resolutionlow" },
      { key: 300, text: "cof.base.resolutionstandard" },
      { key: 600, text: "cof.base.resolutionhigh" },
    ],
  ],
  ["svg", []],
  ["emf", []],
  [
    "tiff",
    [
      { key: 150, text: "cof.base.resolutionlow" },
      { key: 300, text: "cof.base.resolutionstandard" },
      { key: 600, text: "cof.base.resolutionhigh" },
    ],
  ],
]);

const checkOptionsContainsValue = (options: IDropdownOption[], value): boolean => {
  if (options.length === 0) {
    return false;
  }
  return options.some((option) => option.key === value);
};

function getImageExtensionOption(imageExtension) {
  return imageExtension
    ? imageExtensionOptions.find((option) => option.key === imageExtension)
    : imageExtensionOptions.find((option) => option.key === DEFAULT_IMAGE_EXTENSION);
}

function getImageResolutionOption(imageExtension, imageResolution) {
  if (imageExtension) {
    const resolutionOptions = imageResolutionOptionsMap.get(imageExtension) || [];
    return imageResolution
      ? resolutionOptions.find((option) => option.key === imageResolution)
      : resolutionOptions.find((option) => option.key === DEFAULT_IMAGE_RESOLUTION_MAP.get(imageExtension));
  } else {
    const resolutionOptions = imageResolutionOptionsMap.get(DEFAULT_IMAGE_EXTENSION) || [];
    return imageResolution
      ? resolutionOptions.find((option) => option.key === imageResolution)
      : resolutionOptions.find((option) => option.key === DEFAULT_IMAGE_RESOLUTION_MAP.get(DEFAULT_IMAGE_EXTENSION));
  }
}

interface ImageDropdownProps {
  imageExtension: string;
  imageResolution: number;
  disabled: boolean;
  required: boolean;
  onExtensionChange: (option: string | number, modified: boolean) => void;
  onResolutionChange: (option: string | number, modified: boolean) => void;
}

export const ImageDropdown: React.FC<ImageDropdownProps> = ({
  imageExtension,
  imageResolution,
  disabled,
  required,
  onExtensionChange,
  onResolutionChange,
}) => {
  const [selectedImageExtension, setSelectedImageExtension] = useState<IDropdownOption | undefined>(
    getImageExtensionOption(imageExtension)
  );
  const [imageResolutionOptions, setImageResolutionOptions] = useState<IDropdownOption[]>(
    selectedImageExtension ? imageResolutionOptionsMap.get(selectedImageExtension.key as string) : []
  );
  const [selectedImageResolution, setSelectedImageResolution] = useState<IDropdownOption | undefined>(
    getImageResolutionOption(imageExtension, imageResolution)
  );

  useEffect(() => {
    if (selectedImageExtension?.key) {
      setImageResolutionOptions(imageResolutionOptionsMap.get(selectedImageExtension.key as string) || []);
      setSelectedImageResolution(getImageResolutionOption(selectedImageExtension.key, imageResolution));
      onResolutionChange(
        selectedImageExtension.key === imageExtension &&
          checkOptionsContainsValue(imageResolutionOptions, imageResolution)
          ? imageResolution
          : DEFAULT_IMAGE_RESOLUTION_MAP.get(selectedImageExtension.key as string),
        false
      );
    }
  }, [selectedImageExtension]);

  useEffect(() => {
    setSelectedImageExtension(getImageExtensionOption(imageExtension));
    setSelectedImageResolution(getImageResolutionOption(imageExtension, imageResolution));
  }, [imageExtension, imageResolution]);

  const handleChangeImageExtension = (selectedExtension: IDropdownOption | undefined, onExtensionChange) => {
    setSelectedImageExtension(selectedExtension);
    onExtensionChange(selectedExtension.key, true);
  };

  const handleChangeImageResolution = (selectedResolution: IDropdownOption | undefined, onResolutionChange) => {
    setSelectedImageResolution(selectedResolution);
    onResolutionChange(selectedResolution.key, true);
  };

  return (
    <>
      <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
        <Dropdown
          id="ImageExtension"
          data-testid="ImageExtension"
          placeholder={formatMessage("cof.base.select")}
          label={formatMessage("cof.createnewobject.selectimageformat")}
          selectedKey={selectedImageExtension?.key}
          options={imageExtensionOptions.map((o) => ({ ...o, text: formatMessage(o.text) }))}
          onChange={(_event, selectedOption) =>
            handleChangeImageExtension(selectedOption ?? undefined, onExtensionChange)
          }
          responsiveMode={ResponsiveMode.large}
          required={required}
          disabled={disabled}
        />
        {selectedImageExtension?.key &&
          SUPPORTED_ONLY_ON_WORD_DESKTOP.includes(selectedImageExtension?.key as string) && (
            <div style={{ paddingLeft: "0.5rem", color: "lightgray" }}>
              {formatMessage("cof.createnewobject.selectimageformat.info")}
            </div>
          )}
      </div>
      {imageResolutionOptions?.length !== 0 && (
        <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
          <Dropdown
            id="ImageResolution"
            data-testid="ImageResolution"
            placeholder={formatMessage("cof.base.select")}
            label={formatMessage("cof.createnewobject.selectimageresolution")}
            selectedKey={selectedImageResolution?.key}
            options={imageResolutionOptions.map((o) => ({ ...o, text: formatMessage(o.text) }))}
            onChange={(_event, selectedOption) =>
              handleChangeImageResolution(selectedOption ?? undefined, onResolutionChange)
            }
            responsiveMode={ResponsiveMode.large}
            required={required}
            disabled={disabled}
          />
        </div>
      )}
    </>
  );
};
